// Dynamic permission and department handling utilities
// All department, role, and permission data should come from the backend/user object

/**
 * Default admin role names (can be overridden by user object)
 */
export const DEFAULT_ADMIN_ROLES = ['admin', 'sub-admin'];

/**
 * Converts backend permission string to CASL format [action, subject]
 * Supports formats like:
 * - 'dashboard.view' -> ['read', 'Dashboard']
 * - 'leads.read' -> ['read', 'Leads']
 * - 'quotes.approve' -> ['update', 'Quotes']
 * - 'marketing.campaigns' -> ['manage', 'MarketingCampaigns']
 * 
 * This is a flexible parser that handles any resource.action format
 */
export const parsePermission = (permissionString) => {
    if (!permissionString || typeof permissionString !== 'string') {
        return null;
    }

    const parts = permissionString.split('.');
    if (parts.length < 2) {
        return null;
    }

    const [resourceKey, actionKey, ...additionalParts] = parts;

    // Map common action names to CASL actions
    const actionMap = {
        'view': 'read',
        'read': 'read',
        'create': 'create',
        'update': 'update',
        'delete': 'delete',
        'approve': 'update',
        'manage': 'manage',
        'full': 'manage',
        'sync': 'manage',
        'record': 'create'
    };

    const action = actionMap[actionKey] || actionKey;

    // Handle compound permissions (e.g., 'marketing.campaigns', 'marketing.imports')
    let subject;
    if (additionalParts.length > 0) {
        // Compound: 'marketing.campaigns' -> 'MarketingCampaigns'
        const compound = [resourceKey, ...additionalParts]
            .map(part => part.charAt(0).toUpperCase() + part.slice(1))
            .join('');
        subject = compound;
    } else {
        // Simple: 'leads' -> 'Leads'
        subject = resourceKey.charAt(0).toUpperCase() + resourceKey.slice(1);
    }

    return [action, subject];
};

/**
 * Checks if a user has access to a specific department
 * Admin roles (from user.adminRoles or default) can access all departments
 */
export const canAccessDepartment = (userDepartment, targetDepartment, userRole, adminRoles = DEFAULT_ADMIN_ROLES) => {
    if (!userDepartment || !targetDepartment) {
        return false;
    }

    // Admin roles can access all departments
    if (adminRoles.includes(userRole)) {
        return true;
    }

    // Users can only access their own department
    return userDepartment === targetDepartment;
};

/**
 * Extracts department information from user object
 * The user object may contain:
 * - user.department: user's department
 * - user.role: user's role
 * - user.permissions: array of permission strings
 * - user.adminRoles: array of roles that are admin-level (optional)
 * - user.resourceDepartments: mapping of resources to departments (optional)
 */
export const getUserMetadata = (user) => {
    if (!user) {
        return {
            department: null,
            role: 'guest',
            permissions: [],
            adminRoles: DEFAULT_ADMIN_ROLES,
            resourceDepartments: {}
        };
    }

    return {
        department: user.department || null,
        role: user.role || 'guest',
        permissions: Array.isArray(user.permissions) ? user.permissions : [],
        adminRoles: Array.isArray(user.adminRoles) ? user.adminRoles : DEFAULT_ADMIN_ROLES,
        resourceDepartments: user.resourceDepartments || {}
    };
};

/**
 * Gets the department for a resource/subject
 * First checks user.resourceDepartments mapping, then falls back to inference
 */
export const getDepartmentFromSubject = (subject, resourceDepartments = {}) => {
    // Check explicit mapping from user object
    if (resourceDepartments[subject]) {
        return resourceDepartments[subject];
    }

    // If no explicit mapping, return null (resource is department-agnostic)
    // The backend should provide resourceDepartments mapping if department isolation is needed
    return null;
};
